import { FunctionComponent, useMemo, useState } from "react";

import { parse } from "date-fns";

import { ReactComponent as PhoneIcon } from "@assets/icons/phone.svg";

import { SvgIcon } from "@/components/Icon/SvgIcon";

import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { EmailIcon, EmailIconProps } from "@/components/EmailIcon/EmailIcon";
import { AboutSection } from "@/components/IdentityCard/AboutSection";
import { ExpandToggle } from "@/components/IdentityCard/ExpandToggle";
import { WorkHistoryList } from "@/components/IdentityCard/WorkHistoryList";

import { ensureHttpProtocol } from "@/utils/url";

import { Organization, People } from "../../../Apollo/schema/get-people";

interface PersonHeaderProps {
	primaryPerson: People;
	/** Accepted for call-site compatibility; the company renders in its own card below. */
	organization?: Organization;
	getEmailStatus: (emailStatus?: string | null) => EmailIconProps["status"];
}

// Helper component for person avatar with initials fallback
const PersonAvatar: FunctionComponent<{
	firstName?: string | null;
	lastName?: string | null;
	image?: string | null;
}> = ({ firstName, lastName, image }) => {
	const [imageError, setImageError] = useState(false);
	const initials = `${(firstName || "").charAt(0)}${(lastName || "").charAt(0)}`.toUpperCase();

	if (image && !imageError) {
		return (
			<img
				alt={`${firstName} ${lastName}`}
				className="h-[42px] w-[42px] rounded-full object-cover"
				referrerPolicy="no-referrer"
				src={image}
				onError={() => setImageError(true)}
			/>
		);
	}

	return (
		<div className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-blue-action text-base font-semibold text-white">
			{initials || "?"}
		</div>
	);
};

/**
 * Compact person identity card for the Profile Tracker detail view — same
 * layout as the List/Contacts `ListPersonHeader` (unified-prototype v33
 * `lp-person`): avatar, name with the channel icons inline, title, and a
 * location line ending in a "See work history" expander (About + CV).
 */
export const PersonHeader: FunctionComponent<PersonHeaderProps> = ({
	primaryPerson,
	getEmailStatus,
}) => {
	const [expanded, setExpanded] = useState(false);

	const employments = useMemo(() => {
		const cv = [...(primaryPerson.prospectEmployments || [])];

		cv.sort((a, b) => {
			try {
				const aDate = parse(a.startedAt, "yyyy-MM-dd", new Date());
				const bDate = parse(b.startedAt, "yyyy-MM-dd", new Date());

				return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
			} catch {
				return 0;
			}
		});

		return cv;
	}, [primaryPerson.prospectEmployments]);

	// The expander's About block shows the LinkedIn headline; the bio (`about`)
	// renders separately as the Overview tab's Summary — different fields.
	const headlineText = primaryPerson.headline?.trim();
	const hasExpandableContent = employments.length > 0 || !!headlineText;

	if (
		!primaryPerson.name ||
		["SELECT", "Person not found", "Company not found"].includes(primaryPerson.name)
	) {
		return <></>;
	}

	return (
		<div className="mb-6 px-4">
			<div className="flex flex-col rounded-lg border border-gray-200 p-3">
				<div className="flex flex-row items-start gap-2.5">
					{/* Person Avatar */}
					<div className="relative shrink-0">
						<PersonAvatar
							firstName={primaryPerson.firstName}
							image={primaryPerson.image}
							lastName={primaryPerson.lastName}
						/>
						{/* Green status dot */}
						<div className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-green-500" />
					</div>

					{/* Person Info */}
					<div className="flex min-w-0 flex-1 flex-col items-start">
						{/* Name + icon strip cluster together (v33 `lp-name-row`), wrapping as needed */}
						<div className="flex w-full flex-row flex-wrap items-center gap-x-1.5 gap-y-1">
							<span className="max-w-full truncate text-[15px] font-semibold text-gray-900">
								{primaryPerson.name}
							</span>

							<span className="inline-flex flex-row flex-wrap items-center">
								{primaryPerson.linkedinUrl && (
									<a
										href={ensureHttpProtocol(primaryPerson.linkedinUrl)}
										referrerPolicy="no-referrer"
										rel="noreferrer"
										target="_blank"
									>
										<LinkedinIcon status="checked" />
									</a>
								)}
								{primaryPerson.email && (
									<span className="inline-flex">
										<EmailIcon status={getEmailStatus(primaryPerson.emailStatus)} />
									</span>
								)}
								{primaryPerson.phone && (
									<a href={`tel:${primaryPerson.phone}`}>
										<SvgIcon className="mr-1 h-[16px] w-auto" svgIcon={PhoneIcon} />
									</a>
								)}
							</span>
						</div>

						{primaryPerson.title && (
							<div className="mt-0.5 text-xs font-medium text-text-meta">{primaryPerson.title}</div>
						)}

						{(primaryPerson.city || primaryPerson.country || hasExpandableContent) && (
							<div className="mt-1 flex w-full flex-row flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-meta">
								<span className="truncate">
									{primaryPerson.city && primaryPerson.country
										? `${primaryPerson.city}, ${primaryPerson.country}`
										: primaryPerson.city || primaryPerson.country}
								</span>
								{hasExpandableContent && (
									<ExpandToggle
										expanded={expanded}
										showLabelKey="contacts.leadPanel.identity.seeWorkHistory"
										onToggle={() => setExpanded((value) => !value)}
									/>
								)}
							</div>
						)}
					</div>
				</div>

				{expanded && (
					<div className="mt-3 flex flex-col gap-4 border-t border-gray-200 pt-3">
						<AboutSection text={headlineText} />
						<WorkHistoryList employments={employments} />
					</div>
				)}
			</div>
		</div>
	);
};
